<template>
  <div class="font-inter h-full rounded-t-[20px] mt-3">
    <!-- <template v-if="loading">
      <SkeletonLoader v-for="n in 6" :key="n" width="100%" height="171px" />
    </template> -->
    <div v-if="loading">
      <Loader />
    </div>

    <div v-else class="my-6 mx-4 md:mx-8 min-h-screen md:px-0">
      <div
        class="flex flex-col md:flex-row gap-6 md:gap-10 lg:gap-16 items-start"
      >
        <!-- Back Button Column -->
        <aside class="flex-shrink-0">
          <BackButton />
        </aside>

        <!-- Content Column -->
        <main class="flex-1 w-full space-y-6 md:space-y-8">
          <!-- Header Section -->
          <div class="flex flex-col md:flex-row gap-8 items-start relative">
            <!-- Download Button (Absolute on Desktop) -->
            <button
              class="hidden md:flex absolute top-0 right-0 items-center gap-2 px-6 py-2 bg-foreground text-background text-sm font-medium rounded-full hover:opacity-90 transition-opacity"
            >
              <Download class="w-4 h-4" />
              Download profile
            </button>

            <!-- Left Column: Avatar & Availability -->
            <div class="flex flex-col items-center md:items-center gap-4">
              <div class="relative mb-3">
                <img
                  v-if="getProfilePicture()"
                  :src="getProfilePicture()"
                  alt="Profile Picture"
                  class="w-[160px] h-[160px] rounded-full object-cover shadow-sm"
                />
                <UserAvatar
                  v-else
                  :fullName="fullName"
                  class="w-[160px] h-[160px] rounded-full shadow-sm text-4xl"
                />

                <!-- Availability Badge (Overlapping) -->
                <div
                  class="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap flex items-center gap-2 px-4 py-1.5 bg-background border border-border rounded-full shadow-sm cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <div class="w-2 h-2 rounded-full bg-green-500"></div>
                  <span class="text-xs font-medium text-foreground"
                    >Set work availability</span
                  >
                </div>
              </div>

              <!-- Location -->
              <div
                class="flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground cursor-pointer transition-colors mt-2"
              >
                <MapPin class="w-4 h-4" />
                <span class="text-sm font-medium">{{
                  mockData.location || "Set location"
                }}</span>
              </div>
            </div>

            <!-- Right Column: Info & Actions -->
            <div class="flex-1 space-y-6 pt-2">
              <div class="space-y-2 text-center md:text-left">
                <div
                  class="flex items-center justify-center md:justify-start gap-3"
                >
                  <h1 class="text-3xl md:text-4xl font-bold text-foreground">
                    {{ capitalize(user.first_name) }}
                    {{ capitalize(user.last_name) }}
                  </h1>
                  <button
                    @click="openProfileModal('basic_info')"
                    class="p-2 text-muted-foreground hover:bg-muted rounded-full transition-colors hidden md:block"
                  >
                    <Pencil class="w-4 h-4" />
                  </button>
                </div>

                <!-- Headline -->
                <div
                  class="flex items-center justify-center md:justify-start gap-2 text-muted-foreground cursor-pointer hover:text-foreground transition-colors group"
                >
                  <Plus class="w-4 h-4" />
                  <span class="text-sm">{{
                    mockData.headline || "Add headline"
                  }}</span>
                  <Pencil
                    class="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex flex-wrap justify-center md:justify-start gap-3">
                <button
                  @click="openProfileModal('websites')"
                  class="flex items-center gap-2 px-4 py-2 bg-background border border-input hover:bg-muted text-foreground text-sm font-medium rounded-full transition-colors"
                >
                  <Globe class="w-4 h-4" />
                  {{
                    talent.websites && talent.websites.length
                      ? "Manage websites"
                      : "Add website"
                  }}
                </button>
                <button
                  class="flex items-center gap-2 px-4 py-2 bg-background border border-input hover:bg-muted text-foreground text-sm font-medium rounded-full transition-colors"
                >
                  <MessageSquare class="w-4 h-4" />
                  Message
                </button>
                <button
                  class="p-2 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                >
                  <Pencil class="w-4 h-4" />
                </button>
              </div>

              <!-- Role / Experience -->
              <div class="space-y-3 pt-2">
                <p
                  class="text-sm font-medium text-muted-foreground text-center md:text-left"
                >
                  Role/Years of Experience
                </p>
                <div
                  class="flex flex-wrap justify-center md:justify-start gap-2"
                >
                  <div
                    v-for="role in getAvailableRoles()"
                    :key="role"
                    class="flex items-center gap-2 px-4 py-2 bg-muted/30 border border-border rounded-full text-sm"
                  >
                    {{ capitalize(role) }}
                  </div>
                  <button
                    class="flex items-center gap-2 px-4 py-2 bg-background border border-input hover:bg-muted text-foreground text-sm font-medium rounded-full transition-colors"
                  >
                    <Plus class="w-4 h-4" />
                    Add role
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isAdmin || isTalent || isManager || isInvestor || isUser"
            class="space-y-6"
          >
            <!-- Tabs Section with Border -->
            <div
              class="border border-border rounded-xl bg-card overflow-hidden mt-8"
            >
              <Tabs v-model="activeProfileTab" class="w-full">
                <div class="border-b border-border px-6">
                  <TabsList
                    class="flex justify-start h-auto p-0 bg-transparent w-full rounded-none border-none"
                  >
                    <TabsTrigger
                      v-for="tab in profileTabs"
                      :key="tab"
                      :value="tab"
                      class="px-0 py-4 mr-8 text-sm font-medium text-muted-foreground border-b-2 border-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:border-foreground rounded-none transition-all relative top-[1px]"
                    >
                      {{ tab }}
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="About" class="mt-0 space-y-8 p-6">
                  <!-- Bio Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Bio</h3>
                      <button
                        @click="openProfileModal('bio')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Edit bio"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      v-if="user?.bio"
                      class="p-4 border border-border rounded-xl bg-card"
                    >
                      <p class="text-sm text-muted-foreground leading-relaxed">
                        {{ user?.bio }}
                      </p>
                    </div>
                  </div>

                  <!-- Skills Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Skills</h3>
                      <button
                        @click="openProfileModal('skills')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Edit skills"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      class="flex flex-wrap gap-2"
                      v-if="skills && skills.length"
                    >
                      <div
                        v-for="(skill, index) in skills"
                        :key="index"
                        class="px-4 py-2 bg-muted/40 border border-border rounded-full text-sm"
                      >
                        {{ skill.skill_name }}
                      </div>
                      <button
                        @click="addSkills"
                        class="flex items-center justify-center w-8 h-8 rounded-full border border-dashed border-border hover:border-foreground/50 transition-colors"
                      >
                        <Plus class="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                    <button
                      @click="openProfileModal('skills')"
                      class="w-fit flex items-center gap-2 px-4 py-2 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors"
                    >
                      {{
                        skills && skills.length ? "Edit skills" : "Add skills"
                      }}
                    </button>
                  </div>

                  <!-- Online Profiles Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Online Profiles</h3>
                      <button
                        @click="openProfileModal('websites')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Manage profiles"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      v-if="talent.websites && talent.websites.length"
                      class="grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                      <div
                        v-for="(site, index) in talent.websites"
                        :key="index"
                        @click="openUrl(site.url)"
                        class="flex items-center gap-4 p-4 border border-border rounded-xl bg-card hover:bg-muted/10 transition-all cursor-pointer group"
                      >
                        <div class="p-2 bg-muted/30 rounded-lg shrink-0">
                          <Globe class="w-6 h-6 text-muted-foreground/50" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <h4 class="font-bold text-foreground truncate">
                            {{ site.label }}
                          </h4>
                          <p class="text-xs text-muted-foreground truncate">
                            {{ site.url }}
                          </p>
                        </div>
                        <ExternalLink
                          class="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                    </div>
                    <button
                      @click="openProfileModal('websites')"
                      class="w-fit flex items-center gap-2 px-4 py-2 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors"
                    >
                      {{
                        talent.websites && talent.websites.length
                          ? "Manage websites"
                          : "Add website"
                      }}
                    </button>
                  </div>

                  <!-- Projects Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Projects</h3>
                      <button
                        @click="openProfileModal('projects')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Manage projects"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>

                    <div
                      v-if="talentProjects && talentProjects.length"
                      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                      <div
                        v-for="project in talentProjects"
                        :key="project.project_id"
                        @click="goToProject(project)"
                        class="group block"
                        :class="{ 'cursor-pointer': project.project_url }"
                      >
                        <div
                          class="relative overflow-hidden rounded-xl border border-border bg-card hover:shadow-md transition-all"
                        >
                          <div class="aspect-video bg-muted relative">
                            <img
                              v-if="project.project_image"
                              :src="project.project_image"
                              alt="Project Cover"
                              class="w-full h-full object-cover"
                            />
                            <div
                              v-else
                              class="w-full h-full flex items-center justify-center bg-muted text-muted-foreground"
                            >
                              <Briefcase class="w-8 h-8 opacity-20" />
                            </div>
                          </div>
                          <div class="p-4 space-y-2">
                            <h4 class="font-semibold text-foreground truncate">
                              {{ project.project_name }}
                            </h4>
                            <p
                              class="text-xs text-muted-foreground line-clamp-2"
                            >
                              {{ project.description }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Work Experience Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Work Experience</h3>
                      <button
                        @click="openProfileModal('experience')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Manage experience"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      @click="openProfileModal('experience')"
                      class="w-fit flex items-center gap-2 px-4 py-2 bg-background border border-input text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors"
                    >
                      {{
                        talent.experience && talent.experience.length
                          ? "Manage experience"
                          : "Add a job"
                      }}
                    </button>
                    <div
                      v-if="
                        talent.work_experiences &&
                        talent.work_experiences.length
                      "
                      class="space-y-3"
                    >
                      <div
                        v-for="(exp, index) in talent.work_experiences"
                        :key="index"
                        class="flex items-center gap-4 p-4 rounded-2xl border border-border/40 bg-card hover:bg-muted/10 transition-all group"
                      >
                        <div
                          class="w-14 h-14 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden"
                        >
                          <Briefcase class="w-6 h-6 text-muted-foreground/50" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <h4
                            class="text-[16px] font-bold text-foreground truncate"
                          >
                            {{ exp.title }}
                          </h4>
                          <p class="text-[13px] text-muted-foreground truncate">
                            {{ exp.organization }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Education Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Education</h3>
                      <button
                        @click="openProfileModal('education')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Manage education"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      @click="openProfileModal('education')"
                      class="w-fit flex items-center gap-2 px-4 py-2 bg-background border border-input text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors"
                    >
                      {{
                        talent.education && talent.education.length
                          ? "Manage education"
                          : "Add education"
                      }}
                    </button>
                    <div
                      v-if="talent.education && talent.education.length"
                      class="space-y-3"
                    >
                      <div
                        v-for="(edu, index) in talent.education"
                        :key="index"
                        class="flex items-center gap-4 p-4 rounded-2xl border border-border/40 bg-card hover:bg-muted/10 transition-all group"
                      >
                        <div
                          class="w-14 h-14 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden"
                        >
                          <GraduationCap
                            class="w-6 h-6 text-muted-foreground/50"
                          />
                        </div>
                        <div class="flex-1 min-w-0">
                          <h4
                            class="text-[16px] font-bold text-foreground truncate"
                          >
                            {{ edu.degree }}
                          </h4>
                          <p class="text-[13px] text-muted-foreground truncate">
                            {{ edu.school }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Certificates Section -->
                  <div class="space-y-4">
                    <div class="flex items-center gap-3">
                      <h3 class="font-semibold text-lg">Certificates</h3>
                      <button
                        @click="openProfileModal('certificates')"
                        class="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                        title="Manage certificates"
                      >
                        <Pencil class="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      v-if="talent.certificates && talent.certificates.length"
                      class="grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                      <div
                        v-for="cert in talent.certificates"
                        :key="cert.id || cert.certification"
                        class="flex items-start gap-4 p-4 border border-border rounded-xl bg-card transition-all"
                      >
                        <div class="p-2 bg-muted/30 rounded-lg shrink-0">
                          <Medal class="w-6 h-6 text-muted-foreground/50" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <h4 class="font-bold text-foreground truncate">
                            {{ cert.certification }}
                          </h4>
                          <p class="text-xs text-muted-foreground truncate">
                            {{ cert.issued_by }} | {{ cert.year_issued }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      @click="openProfileModal('certificates')"
                      class="w-fit flex items-center gap-2 px-4 py-2 bg-background border border-input text-foreground rounded-full text-sm font-medium hover:bg-muted transition-colors"
                    >
                      {{
                        talent.certificates && talent.certificates.length
                          ? "Manage certificates"
                          : "Add certificates"
                      }}
                    </button>
                  </div>
                  <!-- Deactivate Section -->
                  <div class="pt-8 border-t border-border mt-8">
                    <button
                      @click="deactivateProfile"
                      class="text-sm font-medium text-destructive hover:underline"
                    >
                      Deactivate profile
                    </button>
                  </div>
                </TabsContent>

                <TabsContent value="Achievements" class="mt-0 p-6">
                  <AchievementsTab />
                </TabsContent>
              </Tabs>
            </div>
            <!-- End of bordered container -->
          </div>

          <!-- Glass Overlay -->
          <div
            v-if="isSideMenuOpen"
            class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40"
            @click="closeSideMenu"
          ></div>

          <div
            v-if="isSideMenuOpen"
            class="fixed top-0 right-0 w-full md:w-[500px] h-full bg-background shadow-2xl z-50 transform transition-transform duration-300 text-[14px] flex flex-col border-l border-border"
          >
            <div class="space-y-4">
              <div
                class="py-4 px-4 flex justify-between items-center border-b-[1px] border-grayLight dark:border-gray-800"
              >
                <h3 class="text-lg font-medium text-foreground">
                  {{ selectedProject?.project_name }}
                </h3>

                <div class="flex space-x-2 items-center">
                  <!-- <img
                src="@/assets/tasks/paperclip.svg"
                alt="Options"
                class="h-6 cursor-pointer"
              /> -->
                  <img
                    src="@/assets/projects/options.svg"
                    alt="Options"
                    class="h-6 cursor-pointer"
                  />
                  <div
                    @click="closeSideMenu"
                    class="bg-zinc-100 dark:bg-zinc-800 h-8 w-8 rounded-full flex items-center justify-center cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                  >
                    <!-- <img
                  src="@/assets/tasks/iconoir_cancel.svg"
                  alt="Options"
                  class="h-6 cursor-pointer"
                /> -->
                  </div>
                </div>
              </div>
            </div>
            <div
              class="px-5 py-4 space-y-4 text-muted-foreground text-[13px] overflow-y-auto flex-1"
            >
              <div class="space-y-3">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div
                    class="space-y-4 items-center border border-border py-4 px-4 rounded-xl bg-card/50"
                  >
                    <p>Status</p>
                    <span class="text-[15px] font-semibold text-foreground">{{
                      selectedProject?.project_status
                    }}</span>
                  </div>
                  <div
                    class="space-y-4 items-center border border-border py-4 px-4 rounded-xl bg-card/50"
                  >
                    <p>Timeline</p>
                    <span class="text-[15px] font-semibold text-foreground"
                      >{{ selectedProject?.project_start_date }} -
                      {{ selectedProject.project_end_date }}</span
                    >
                  </div>
                  <div
                    class="space-y-4 items-center border border-border py-4 px-4 rounded-xl bg-card/50"
                  >
                    <p>Task Completed</p>
                    <span class="text-[15px] font-semibold text-foreground">{{
                      selectedProject?.project_status
                    }}</span>
                  </div>
                </div>

                <div
                  class="space-y-4 items-center border border-zinc-200 dark:border-zinc-800 py-4 px-4 rounded-xl"
                >
                  <div class="items-center">
                    <p
                      class="font-semibold text-bodyText dark:text-primaryLight text-[15px]"
                    >
                      Project Description
                    </p>
                  </div>
                  <span class="leading-relaxed">{{
                    selectedProject?.project_description
                  }}</span>
                </div>
                <div
                  class="space-y-4 items-center border border-border py-4 px-4 rounded-xl bg-card/50"
                >
                  <div class="items-center">
                    <p>Technologies Used</p>
                  </div>
                  <span class="text-foreground">{{
                    selectedMember?.talent_email || selectedMember?.user.email
                  }}</span>
                </div>

                <div
                  class="space-y-4 items-center border border-border py-4 px-4 rounded-xl bg-card/50"
                >
                  <div class="items-center">
                    <p>Team Members</p>
                  </div>
                  <span class="text-foreground">{{
                    selectedMember?.talent_phone
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- New Modular Modal Shell -->
          <ProfileModalShell
            :show="showProfileModal"
            :title="modalTitle"
            :subtitle="modalSubtitle"
            :loading="isModalLoading"
            @close="closeProfileModal"
            @discard="closeProfileModal"
            @save="saveProfileModal"
          >
            <!-- Bio Form -->
            <BioForm v-if="modalType === 'bio'" v-model:value="modalData" />

            <!-- Skills Manager -->
            <SkillsManager
              v-else-if="modalType === 'skills'"
              v-model:value="modalData"
              :userRole="capitalize(talent.position) || 'Frontend Engineer'"
            />

            <!-- Experience Manager -->
            <div v-else-if="modalType === 'experience'">
              <SectionListManager
                ref="listManager"
                v-model:items="modalData"
                @state-change="onManagerStateChange"
                @add-item="onItemAdd"
                @update-item="onItemUpdate"
                @remove-item="onItemRemove"
                itemName="Work experience"
                titleKey="title"
                subtitleKey="organization"
              >
                <template #form="{ item, update }">
                  <ExperienceForm :item="item" @update="update" />
                </template>
              </SectionListManager>
            </div>

            <!-- Projects Manager -->
            <div v-else-if="modalType === 'projects'">
              <SectionListManager
                ref="listManager"
                v-model:items="modalData"
                @state-change="onManagerStateChange"
                @add-item="onItemAdd"
                @update-item="onItemUpdate"
                @remove-item="onItemRemove"
                itemName="Project"
                titleKey="project_name"
                subtitleKey="project_url"
              >
                <template #form="{ item, update }">
                  <ProjectForm :item="item" @update="update" />
                </template>
              </SectionListManager>
            </div>

            <!-- Education Manager -->
            <div v-else-if="modalType === 'education'">
              <SectionListManager
                ref="listManager"
                v-model:items="modalData"
                @state-change="onManagerStateChange"
                @add-item="onItemAdd"
                @update-item="onItemUpdate"
                @remove-item="onItemRemove"
                itemName="Education"
                titleKey="degree"
                subtitleKey="school"
              >
                <template #form="{ item, update }">
                  <EducationForm :item="item" @update="update" />
                </template>
              </SectionListManager>
            </div>

            <!-- Websites Manager -->
            <div v-else-if="modalType === 'websites'">
              <SectionListManager
                ref="listManager"
                v-model:items="modalData"
                @state-change="onManagerStateChange"
                @add-item="onItemAdd"
                @update-item="onItemUpdate"
                @remove-item="onItemRemove"
                itemName="Online profile"
                titleKey="label"
                subtitleKey="url"
              >
                <template #item-icon>
                  <Globe class="w-6 h-6 text-muted-foreground/50" />
                </template>
                <template #form="{ item, update }">
                  <WebsitesForm :item="item" @update="update" />
                </template>
              </SectionListManager>
            </div>

            <!-- Basic Info Modal -->
            <div v-if="modalType === 'basic_info'">
              <BasicInfoForm
                v-model:firstName="modalData.first_name"
                v-model:lastName="modalData.last_name"
              />
            </div>

            <!-- Certificates Manager -->
            <div v-else-if="modalType === 'certificates'">
              <SectionListManager
                ref="listManager"
                v-model:items="modalData"
                @state-change="onManagerStateChange"
                @add-item="onItemAdd"
                @update-item="onItemUpdate"
                @remove-item="onItemRemove"
                itemName="Certificate"
                titleKey="certification"
                subtitleKey="issued_by"
              >
                <template #item-icon>
                  <Medal class="w-6 h-6 text-muted-foreground/50" />
                </template>
                <template #form="{ item, update }">
                  <CertificatesForm :item="item" @update="update" />
                </template>
              </SectionListManager>
            </div>
          </ProfileModalShell>
        </main>
      </div>
    </div>
  </div>
  <div
    v-if="copyTooltip.show"
    class="fixed z-[9999] bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
    :style="{ left: copyTooltip.x + 'px', top: copyTooltip.y + 'px' }"
  >
    {{ copyTooltip.text }}
  </div>
</template>

<script>
import apiClient from "@/axios";
import UserAvatar from "../auth/UserAvatar.vue";
// import SkeletonLoader from "../SkeletonLoader.vue";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BackButton from "../BackButton.vue";
import Loader from "../ui/Loader.vue";
import {
  MapPin,
  Globe,
  MessageSquare,
  Download,
  Pencil,
  Plus,
  Briefcase,
  GraduationCap,
  Medal,
  ExternalLink,
} from "lucide-vue-next";
// import { createTabStateManager } from "@/utils/tabStateManager";
// import InvestorProjects from "../project/InvestorProjects.vue";

import ProfileModalShell from "../modals/ProfileModalShell.vue";
import BioForm from "../modals/profile/BioForm.vue";
import SkillsManager from "../modals/profile/SkillsManager.vue";
import SectionListManager from "../modals/profile/SectionListManager.vue";
import ExperienceForm from "../modals/profile/ExperienceForm.vue";
import ProjectForm from "../modals/profile/ProjectForm.vue";
import EducationForm from "../modals/profile/EducationForm.vue";
import WebsitesForm from "../modals/profile/WebsitesForm.vue";
import CertificatesForm from "../modals/profile/CertificatesForm.vue";
import BasicInfoForm from "../modals/profile/BasicInfoForm.vue";
import AchievementsTab from "./AchievementsTab.vue";

export default {
  name: "TalentProfile",
  components: {
    UserAvatar,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    ProfileModalShell,
    BioForm,
    SkillsManager,
    SectionListManager,
    ExperienceForm,
    ProjectForm,
    EducationForm,
    WebsitesForm,
    CertificatesForm,
    BasicInfoForm,
    AchievementsTab,
    BackButton,
    Loader,
    MapPin,
    Globe,
    MessageSquare,
    Download,
    Pencil,
    Plus,
    Briefcase,
    GraduationCap,
    Medal,
    ExternalLink,
  },
  data() {
    return {
      activeTab: null,
      activeProfileTab: "About",
      // activeTaskTab: "To-do",
      activeInvestorProfileTab: "Invested Projects",

      taskTabs: ["To-do", "Done"],
      investorProfileTabs: ["Invested Projects", "Overview"],
      // projects: [],
      talentProjects: [],
      managerProjects: [],
      investorProjects: [],
      talent: "",
      investor: "",
      manager: "",
      user: {},
      completedTasksCount: 0,
      allNonCompletedTasks: [],
      loading: true,
      newBio: {
        bio: "",
      },
      showProfileModal: false,
      modalType: "",
      modalTitle: "",
      modalSubtitle: "",
      modalData: null,
      isModalLoading: false,
      managerState: "list",
      isSideMenuOpen: false,

      selectedProject: null,
      selectedProjectMembers: [],

      mainTabStateManager: null,
      deviceTabStateManager: null,

      // Storage listeners
      mainTabStorageListener: null,
      deviceTabStorageListener: null,
      joinedProjects: [],
      totalAmountInvested: 0,
      successMessage: "",
      errorMessage: "",
      copyTooltip: { show: false, text: "", x: 0, y: 0 },
      mockData: {
        headline: "",
        location: "",
        availability: "Available",
        website: "",
      },
    };
  },
  watch: {
    activeProfileTab: {
      handler(newTab) {
        if (this.mainTabStateManager && newTab) {
          this.mainTabStateManager.setActiveTab(newTab);
          console.log("Main settings tab changed to:", newTab);
        }
      },
      immediate: false,
    },

    // Watch device settings sub-tab changes
    activeTaskTab: {
      handler(newTab) {
        if (this.deviceTabStateManager && newTab) {
          this.deviceTabStateManager.setActiveTab(newTab);
          console.log("Device settings tab changed to:", newTab);
        }
      },
      immediate: false,
    },

    // Reinitialize when user changes
    "$store.state.user": {
      handler(newUser, oldUser) {
        if (newUser && newUser !== oldUser) {
          this.initAllTabStates();
        }
      },
      deep: true,
    },
  },
  computed: {
    isAdmin() {
      return this.getAvailableRoles().includes("admin");
    },
    isManager() {
      return this.getAvailableRoles().includes("manager");
    },
    isTalent() {
      return this.getAvailableRoles().includes("talent");
    },
    isInvestor() {
      return this.getAvailableRoles().includes("investor");
    },
    isUser() {
      return this.hasRole("user");
    },

    fullName() {
      // 1. Prioritize user fields for the avatar name
      if (this.user?.first_name || this.user?.last_name) {
        return `${this.user.first_name || ""} ${
          this.user.last_name || ""
        }`.trim();
      }

      // 2. Fallback to talent fields if available
      if (this.talent?.talent_first_name || this.talent?.talent_last_name) {
        return `${this.talent.talent_first_name || ""} ${
          this.talent.talent_last_name || ""
        }`.trim();
      }

      return "";
    },
    completedProjectsCount() {
      if (
        !this.talent ||
        !this.talent.projects ||
        !this.talent.projects.length
      ) {
        return 0;
      }

      return this.talent.projects.filter(
        (project) => project.project_status === "Completed",
      ).length;
    },

    userIdentifier() {
      return this.user?.user_id || this.$store.state.user?.user_id || "default";
    },

    joinedProjectsCount() {
      return Array.isArray(this.joinedProjects)
        ? this.joinedProjects.length
        : 0;
    },
    profileTabs() {
      return ["About", "Achievements"];
    },
  },

  methods: {
    initAllTabStates() {
      this.cleanupStorageListeners();
      this.initMainTabState();
      this.initTaskTabState();
      this.initInvestorTabState();
    },

    initMainTabState() {
      // Tab state manager is disabled - uncomment when needed
      // const userSpecificKey = `profile_mainTab_${this.userIdentifier}`;
      // this.mainTabStateManager = createTabStateManager(
      //   userSpecificKey,
      //   "About",
      //   this.profileTabs,
      // );
      // this.activeProfileTab = this.mainTabStateManager.getActiveTab();
      // this.mainTabStorageListener = (e) => {
      //   if (e.key === userSpecificKey && e.newValue) {
      //     this.activeProfileTab = e.newValue;
      //   }
      // };
      // window.addEventListener("storage", this.mainTabStorageListener);
    },

    initTaskTabState() {
      // Tab state manager is disabled - uncomment when needed
      // const userSpecificKey = `profile_taskTab_${this.userIdentifier}`;
      // this.taskTabStateManager = createTabStateManager(
      //   userSpecificKey,
      //   "To-do",
      //   this.taskTabs,
      // );
      // this.activeTaskTab = this.taskTabStateManager.getActiveTab();
      // this.taskTabStorageListener = (e) => {
      //   if (e.key === userSpecificKey && e.newValue) {
      //     this.activeTaskTab = e.newValue;
      //   }
      // };
      // window.addEventListener("storage", this.taskTabStorageListener);
    },

    initInvestorTabState() {
      // Tab state manager is disabled - uncomment when needed
      // const userSpecificKey = `profile_investorTab_${this.userIdentifier}`;
      // this.investorTabStateManager = createTabStateManager(
      //   userSpecificKey,
      //   "Invested Projects",
      //   this.investorProfileTabs,
      // );
      // this.activeInvestorProfileTab = this.investorTabStateManager.getActiveTab();
      // this.investorTabStorageListener = (e) => {
      //   if (e.key === userSpecificKey && e.newValue) {
      //     this.activeInvestorProfileTab = e.newValue;
      //   }
      // };
      // window.addEventListener("storage", this.investorTabStorageListener);
    },

    cleanupStorageListeners() {
      if (this.mainTabStorageListener) {
        window.removeEventListener("storage", this.mainTabStorageListener);
      }
      if (this.taskTabStorageListener) {
        window.removeEventListener("storage", this.taskTabStorageListener);
      }
      if (this.investorTabStorageListener) {
        window.removeEventListener("storage", this.investorTabStorageListener);
      }
    },

    cleanupOldTabPreferences() {
      // Clean up any old non-user-specific keys
      const keysToCleanup = [
        "activeProfileTab",
        "activeTaskTab",
        "activeInvestorProfileTab",
      ];
      keysToCleanup.forEach((key) => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
    },

    openSideMenu(project) {
      this.selectedProject = { ...project };
      this.isSideMenuOpen = true;

      // Fetch members when a project is selected
      if (project && project.project_id) {
        this.fetchProjectMembers(project.project_id);
      }
    },

    goToProject(project) {
      if (project.project_url) {
        window.open(project.project_url, "_blank");
      }
    },

    closeSideMenu() {
      this.isSideMenuOpen = false;
      this.selectedMember = null;
      this.showSuccess = "";
      this.successMessage = "";
      this.errorMessage = "";
    },
    openProfileModal(type) {
      this.modalType = type;
      this.showProfileModal = true;

      switch (type) {
        case "bio":
          this.modalTitle = "Bio";
          this.modalSubtitle = "Write a brief introduction about yourself.";
          this.modalData = this.user?.bio || "";
          break;
        case "skills":
          this.modalTitle = "Skills";
          this.modalSubtitle =
            "Add at least 5 skills. You can also get your top 3 skills starred.";
          this.modalData = [...(this.skills || [])];
          break;
        case "experience":
          this.modalTitle = "Work Experience";
          this.modalSubtitle = "Add a job";
          this.modalData = [...(this.talent.work_experiences || [])];
          break;
        case "projects":
          this.modalTitle = "Projects";
          this.modalSubtitle =
            "Add your projects you have worked on in the past";
          this.modalData = [...(this.talentProjects || [])];
          break;
        case "education":
          this.modalTitle = "Education";
          this.modalSubtitle = "Add a degree";
          this.modalData = [...(this.talent.education || [])];
          break;
        case "websites":
          this.modalTitle = "Websites";
          this.modalSubtitle = "Add your online profiles";
          this.modalData = [...(this.talent.websites || [])];
          break;
        case "certificates":
          this.modalTitle = "Certificates";
          this.modalSubtitle = "Add a certificate";
          this.modalData = [...(this.talent.certificates || [])];
          break;
        case "basic_info":
          this.modalTitle = "Basic Information";
          this.modalSubtitle = "Update your name and other basic details.";
          this.modalData = {
            first_name: this.user.first_name || "",
            last_name: this.user.last_name || "",
            phone: this.user.phone || "",
            bio: this.user.bio || "",
          };
          break;
      }
    },

    onManagerStateChange(state) {
      this.managerState = state;
    },

    closeProfileModal() {
      if (this.$refs.listManager && this.managerState === "form") {
        this.$refs.listManager.cancelForm();
        return;
      }
      this.showProfileModal = false;
      this.modalType = "";
      this.modalData = null;
      this.managerState = "list";
    },

    async saveProfileModal() {
      if (
        this.$refs.listManager &&
        (this.managerState === "form" || this.managerState === "delete")
      ) {
        if (this.managerState === "form") {
          this.$refs.listManager.saveItem();
        } else {
          this.$refs.listManager.removeItem();
        }
        return;
      }

      this.isModalLoading = true;
      try {
        let endpoint = "/web/profile/me";
        let method = "put";
        let payload = {};

        if (this.modalType === "bio") {
          endpoint = "/web/profile/bio";
          method = "patch";
          payload = { bio: this.modalData, headline: this.mockData.headline };
        } else if (this.modalType === "skills") {
          endpoint = "/web/profile/skills";
          method = "post";
          payload = {
            skill_ids: this.modalData
              .map((s) => s.skill_id || s.id)
              .filter((id) => id != null),
          };
        } else if (this.modalType === "basic_info") {
          endpoint = `/web/users/${this.user.user_id}`;
          method = "put";
          payload = {
            first_name: this.modalData.first_name,
            last_name: this.modalData.last_name,
            phone: this.modalData.phone,
            bio: this.modalData.bio || this.user.bio || "",
            comment: "", // Optional internal comment
          };
        } else if (this.modalType === "websites") {
          this.showProfileModal = false;
          return;
        } else {
          this.showProfileModal = false;
          return;
        }

        await apiClient[method](endpoint, payload);
        this.$toast.success(
          `${this.capitalize(this.modalType)} updated successfully`,
        );
        await this.fetchProfileData();
        this.showProfileModal = false;
        this.modalType = "";
        this.modalData = null;
        this.managerState = "list";
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isModalLoading = false;
      }
    },

    // Granular Item Handlers
    async onItemAdd(item) {
      const endpoint = this.getSectionEndpoint();
      if (!endpoint) return;

      this.isModalLoading = true;
      try {
        let payload = item;
        let config = {};

        /*
        // Handling for projects with images
        const isProjectFile = this.modalType === "projects" && item.project_image && (
          item.project_image instanceof File || 
          (typeof item.project_image === "object" && item.project_image.name)
        );

        if (isProjectFile) {
          const formData = new FormData();
          Object.keys(item).forEach((key) => {
            if (item[key] !== null && item[key] !== undefined) {
              if (key === "project_image") {
                formData.append("cover_image", item[key]);
              } else {
                formData.append(key, item[key]);
              }
            }
          });
          payload = formData;
          config = {
            headers: { "Content-Type": "multipart/form-data" },
          };
        }
        */

        // TEMPORARY: Force project_image to empty string for testing
        if (this.modalType === "projects") {
          payload = { ...item, project_image: "" };
        }

        await apiClient.post(endpoint, payload, config);
        this.$toast.success(
          "Success",
          `${this.capitalize(this.modalType)} added successfully`,
        );
        this.$refs.listManager.commitSave();
        await this.fetchProfileData();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isModalLoading = false;
      }
    },

    async onItemUpdate({ item }) {
      const endpoint = this.getSectionEndpoint();
      const idKey = this.getSectionIdKey();
      if (!endpoint || !item[idKey]) return;

      this.isModalLoading = true;
      try {
        let payload = item;
        let config = {};

        /*
        // Handling for projects with images
        if (
          this.modalType === "projects" &&
          item.project_image instanceof File
        ) {
          const formData = new FormData();
          Object.keys(item).forEach((key) => {
            if (item[key] !== null && item[key] !== undefined) {
              formData.append(key, item[key]);
            }
          });
          payload = formData;
          config = {
            headers: { "Content-Type": "multipart/form-data" },
          };
        }
        */

        // TEMPORARY: Force project_image to empty string for testing
        if (this.modalType === "projects") {
          payload = { ...item, project_image: "" };
        }

        await apiClient.patch(`${endpoint}/${item[idKey]}`, payload, config);
        this.$toast.success(
          "Success",
          `${this.capitalize(this.modalType)} updated successfully`,
        );
        this.$refs.listManager.commitSave();
        await this.fetchProfileData();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isModalLoading = false;
      }
    },

    async onItemRemove({ item }) {
      const endpoint = this.getSectionEndpoint();
      const idKey = this.getSectionIdKey();
      if (!endpoint || !item[idKey]) return;

      this.isModalLoading = true;
      try {
        await apiClient.delete(`${endpoint}/${item[idKey]}`);
        this.$toast.success(
          "Success",
          `${this.capitalize(this.modalType)} removed successfully`,
        );
        this.$refs.listManager.commitRemove();
        await this.fetchProfileData();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isModalLoading = false;
      }
    },

    getSectionEndpoint() {
      const mapping = {
        experience: "/web/profile/work-experience",
        projects: "/web/profile/portfolio-projects",
        education: "/web/profile/education",
        certificates: "/web/profile/certificates",
        websites: "/web/profile/websites",
      };
      return mapping[this.modalType];
    },

    getSectionIdKey() {
      const mapping = {
        experience: "experience_id",
        projects: "project_id",
        education: "education_id",
        certificates: "id", // Based on line 334 in template
        websites: "id",
      };
      return mapping[this.modalType];
    },

    handleApiError(error) {
      console.error("API Error:", error);
      const message = error.response?.data?.message || "Operation failed";
      this.$toast.error("Error", message);
      this.errorMessage = message;
    },

    async deactivateProfile() {
      if (!confirm("Are you sure you want to deactivate your profile?")) return;
      try {
        await apiClient.post("/web/profile/deactivate");
        this.$toast.success("Profile deactivated successfully");
        this.$router.push("/login");
      } catch (error) {
        this.handleApiError(error);
      }
    },
    capitalize(string) {
      if (!string) return "";
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    async submitModal() {
      this.showError = false;
      this.errorMessage = "";
      try {
        const newProjectWithManager = {
          ...this.newProject,
          manager_id: this.selectedManagerId,
        };
        const response = await apiClient.post(
          "/web/projects",
          newProjectWithManager,
        );
        const createdProject = response.data.data;

        this.$emit("projectCreated", "Project created successfully!");
        this.$emit("refresh");
        this.closeModal();

        if (createdProject && createdProject.project_id) {
          this.$router.push(`/projects/${createdProject.project_id}`);
        }
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message || "An unexpected error occurred.";
        console.error("Error creating project:", error);
      }
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },
    async fetchProfileData() {
      try {
        const profileResponse = await apiClient.get("/web/profile/me");
        const respData = profileResponse.data.data;
        this.talent = respData.talent || {};
        this.user = respData.user || {};
        this.skills = respData.skills || [];
        this.talent.education = respData.education || [];
        this.talent.certificates = respData.certificates || [];
        this.talent.work_experiences = respData.work_experiences || [];
        this.talent.portfolio_projects = respData.portfolio_projects || [];
        this.talent.websites = respData.websites || [];

        this.talentProjects = this.talent.portfolio_projects.map((p) => ({
          ...p,
          description: p.description || p.project_description,
          project_image: p.project_image || p.cover_image,
        }));
        this.managerProjects = this.talent.manager_projects || [];

        this.fetchCompletedTasks();
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchCompletedTasks() {
      try {
        let totalCompletedTasks = 0;
        let totalNonCompletedTasks = 0;

        if (
          this.talent &&
          this.talent.projects &&
          this.talent.projects.length > 0
        ) {
          for (const project of this.talent.projects) {
            const response = await apiClient.get(
              `/web/projects/${project.project_id}/tasks`,
            );
            const tasks = response.data.data || [];

            console.log(`Tasks for project ${project.project_id}:`, tasks);
            // Filter tasks that belong to this talent and are completed
            const completedTasks = tasks.filter(
              (task) => task.task_status === "Completed",
              // task.assigned_to === this.talent.talent_id
            );

            const nonCompletedTasks = tasks.filter(
              (task) =>
                // task.task_status === 'In Progress' || task.task_status === 'Not Started'
                task.task_status === "Not Started",
            );

            totalNonCompletedTasks += nonCompletedTasks.length;
            totalCompletedTasks += completedTasks.length;
          }
        }
        this.allNonCompletedTasks = totalNonCompletedTasks;
        this.completedTasksCount = totalCompletedTasks;
      } catch (error) {
        console.error("Error fetching tasks for projects:", error);
        this.completedTasksCount = 0; // Set to 0 on error
      }
    },
    async fetchProjectMembers(projectId) {
      try {
        const response = await apiClient.get(
          `/web/projects/${projectId}/project_members/`,
        );
        this.members = response.data.data;
      } catch (error) {
        console.error("Error fetching members data:", error);
      }
    },

    openEmailClient(email) {
      const subject = encodeURIComponent("Contact from Profile");
      const body = encodeURIComponent(
        "Hi,\n\nI found your profile and would like to get in touch.\n\nBest regards",
      );
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    },

    openUrl(url) {
      if (url) {
        // Add https:// if no protocol is specified
        const fullUrl = url.startsWith("http") ? url : `https://${url}`;
        window.open(fullUrl, "_blank");
      }
    },
    getAvailableRoles() {
      // 1. Check current component state user roles
      if (this.user && this.user.role && Array.isArray(this.user.role)) {
        return this.user.role;
      }
      if (this.user && this.user.roles && Array.isArray(this.user.roles)) {
        return this.user.roles;
      }

      // 2. Check store user roles
      const storeUser = this.$store.getters.currentUser;
      if (storeUser && storeUser.role && Array.isArray(storeUser.role)) {
        return storeUser.role;
      }
      if (storeUser && storeUser.roles && Array.isArray(storeUser.roles)) {
        return storeUser.roles;
      }

      // 3. Check legacy localStorage
      const roles = localStorage.getItem("availableRoles");
      if (roles) {
        try {
          return JSON.parse(roles);
        } catch (e) {
          console.warn("Failed to parse availableRoles from localStorage");
        }
      }

      // 4. Fallback to single role from store
      const singleRole = this.$store.state.role;
      if (singleRole) return [singleRole];

      return [];
    },

    hasRole(role) {
      return this.getAvailableRoles().includes(role);
    },
    async fetchCurrentUser() {
      try {
        const response = await apiClient.post("/current_user");
        this.user = response.data.user || {};
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },
    getProfilePicture() {
      return this.user.profile_picture || null;
    },
    async copyWithTooltip(event, text, message) {
      try {
        await navigator.clipboard.writeText(text);
        this.showTooltip(event, message);
      } catch (err) {
        console.error("Failed to copy: ", err);
        this.fallbackCopyWithTooltip(event, text, message);
      }
    },

    fallbackCopyWithTooltip(event, text, message) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        if (successful) {
          this.showTooltip(event, message);
        }
      } catch (err) {
        console.error("Fallback copy failed", err);
      }

      document.body.removeChild(textArea);
    },

    showTooltip(event, text) {
      const rect = event.target.getBoundingClientRect();
      this.copyTooltip = {
        show: true,
        text: text,
        x: rect.left + rect.width / 2 - 40, // Center tooltip above icon
        y: rect.top - 35, // Position above the icon
      };

      setTimeout(() => {
        this.copyTooltip.show = false;
      }, 2000);
    },
  },
  async mounted() {
    if (this.isInvestor) {
      this.activeTab = "invested_projects";
    } else if (this.isTalent || this.isAdmin || this.isManager) {
      this.activeTab = "my_tasks";
    }

    await this.fetchCurrentUser(); // Ensure we have user data and roles
    await this.fetchProfileData();
    this.fetchCompletedTasks();
    // fetchProjectMembers is called when a project is selected in openSideMenu()
  },
  created() {
    // this.fetchProfileData();
    // Initialize tab state first (after user data is available)
    this.$nextTick(() => {
      this.initAllTabStates();
    });

    // Clean up old preferences
    this.cleanupOldTabPreferences();
  },
  beforeUnmount() {
    // Use beforeDestroy() if using Vue 2
    this.cleanupStorageListeners();
  },
};
</script>
